import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Eye, X, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { buildErrorToast } from '@/lib/apiError';
import { useAuthStore } from '@/stores/auth.store';
import {
  getEmailTemplateLibrary,
  createEmailTemplateLibrary,
  updateEmailTemplateLibrary,
  toggleEmailTemplateLibrary,
  deleteEmailTemplateLibrary,
  type IEmailTemplateLibrary,
  type ICreateEmailTemplateLibraryDto,
} from '@/api/email.api';

const EMPTY_FORM: ICreateEmailTemplateLibraryDto = {
  code: '',
  name: '',
  subject: '',
  body: '',
  fromAddress: '',
  notes: '',
};

export function EmailTemplateLibraryTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isSuperAdmin = user?.roles?.includes('SUPER_ADMIN') ?? false;

  const [previewTemplate, setPreviewTemplate] = useState<IEmailTemplateLibrary | null>(null);
  const [editTarget, setEditTarget] = useState<IEmailTemplateLibrary | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<IEmailTemplateLibrary | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<ICreateEmailTemplateLibraryDto>(EMPTY_FORM);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['email-template-library'],
    queryFn: getEmailTemplateLibrary,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['email-template-library'] });
    queryClient.invalidateQueries({ queryKey: ['daily-report-allowed-templates'] });
  };

  const createMutation = useMutation({
    mutationFn: createEmailTemplateLibrary,
    onSuccess: () => { toast({ title: 'Template created' }); invalidate(); setShowCreate(false); setForm(EMPTY_FORM); },
    onError: (err) => buildErrorToast(toast)(err, 'Failed to create template'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, dto }: { id: string; dto: Omit<ICreateEmailTemplateLibraryDto, 'code'> }) =>
      updateEmailTemplateLibrary(id, dto),
    onSuccess: () => { toast({ title: 'Template updated' }); invalidate(); setEditTarget(null); },
    onError: (err) => buildErrorToast(toast)(err, 'Failed to update template'),
  });

  const toggleMutation = useMutation({
    mutationFn: toggleEmailTemplateLibrary,
    onSuccess: () => { invalidate(); },
    onError: (err) => buildErrorToast(toast)(err, 'Failed to toggle template'),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteEmailTemplateLibrary,
    onSuccess: () => { toast({ title: 'Template deleted' }); invalidate(); setDeleteTarget(null); },
    onError: (err) => buildErrorToast(toast)(err, 'Failed to delete template'),
  });

  function openEdit(tpl: IEmailTemplateLibrary) {
    setEditTarget(tpl);
    setForm({ code: tpl.code, name: tpl.name, subject: tpl.subject, body: tpl.body, fromAddress: tpl.fromAddress ?? '', notes: tpl.notes ?? '' });
  }

  function handleFormChange(field: keyof ICreateEmailTemplateLibraryDto, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  if (isLoading) return <p className="text-muted-foreground py-4">Loading templates...</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Email Template Library</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Templates used for Daily Report and custom email flows. Whitelist is configured at S29.
          </p>
        </div>
        {isSuperAdmin && (
          <Button size="sm" onClick={() => { setShowCreate(true); setForm(EMPTY_FORM); }}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            New Template
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="border-b text-left text-muted-foreground text-xs">
              <th className="px-3 py-2">Code</th>
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Subject</th>
              <th className="px-3 py-2 w-24">Status</th>
              <th className="px-3 py-2 w-36"></th>
            </tr>
          </thead>
          <tbody>
            {templates.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground italic">
                  No templates yet.
                </td>
              </tr>
            )}
            {templates.map((tpl) => (
              <tr key={tpl.id} className="border-b last:border-0 hover:bg-accent/20">
                <td className="px-3 py-2 font-mono text-xs">{tpl.code}</td>
                <td className="px-3 py-2 font-medium">{tpl.name}</td>
                <td className="px-3 py-2 text-muted-foreground truncate max-w-xs">{tpl.subject}</td>
                <td className="px-3 py-2">
                  {tpl.isDisabled ? (
                    <Badge variant="secondary" className="text-xs">Disabled</Badge>
                  ) : (
                    <Badge variant="default" className="text-xs bg-green-100 text-green-700 hover:bg-green-100">Active</Badge>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2 justify-end">
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => setPreviewTemplate(tpl)} title="Preview">
                      <Eye className="h-4 w-4" />
                    </Button>
                    {isSuperAdmin && (
                      <>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(tpl)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm" variant="ghost" className="h-8 w-8 p-0"
                          onClick={() => toggleMutation.mutate(tpl.id)}
                          disabled={toggleMutation.isPending}
                          title={tpl.isDisabled ? 'Enable' : 'Disable'}
                        >
                          {tpl.isDisabled
                            ? <ToggleLeft className="h-6 w-6 text-muted-foreground" />
                            : <ToggleRight className="h-6 w-6 text-green-600" />}
                        </Button>
                        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive" onClick={() => setDeleteTarget(tpl)} title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create / Edit modal */}
      <Dialog
        open={showCreate || !!editTarget}
        onOpenChange={(open) => { if (!open) { setShowCreate(false); setEditTarget(null); } }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editTarget ? 'Edit Template' : 'New Template'}</DialogTitle>
          </DialogHeader>
          <DialogBody className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium">Code <span className="text-destructive">*</span></label>
                <Input
                  className="h-8 text-sm font-mono uppercase"
                  placeholder="DAILY_REPORT_DEFAULT"
                  value={form.code}
                  onChange={(e) => handleFormChange('code', e.target.value)}
                  disabled={!!editTarget}
                />
                {!editTarget && <p className="text-xs text-muted-foreground">Unique, uppercase. Cannot be changed after creation.</p>}
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Name <span className="text-destructive">*</span></label>
                <Input
                  className="h-8 text-sm"
                  placeholder="Daily Client Attendance Report"
                  value={form.name}
                  onChange={(e) => handleFormChange('name', e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Subject <span className="text-destructive">*</span></label>
              <Input
                className="h-8 text-sm"
                placeholder="Daily Attendance Report — {{ClientName}} — {{ReportDate}}"
                value={form.subject}
                onChange={(e) => handleFormChange('subject', e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Merge tags: {'{{ClientName}}'}, {'{{ReportDate}}'}, {'{{StaffCount}}'}, {'{{LeaveCount}}'}, {'{{AbsentCount}}'}</p>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium">Body (HTML) <span className="text-destructive">*</span></label>
              <textarea
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-xs font-mono resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                style={{ minHeight: 200 }}
                placeholder="<!-- HTML email body -->"
                value={form.body}
                onChange={(e) => handleFormChange('body', e.target.value)}
                spellCheck={false}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium">From Address</label>
                <Input className="h-8 text-sm" placeholder="noreply@example.com" value={form.fromAddress ?? ''} onChange={(e) => handleFormChange('fromAddress', e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium">Notes</label>
                <Input className="h-8 text-sm" placeholder="Internal notes..." value={form.notes ?? ''} onChange={(e) => handleFormChange('notes', e.target.value)} />
              </div>
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditTarget(null); }}>Cancel</Button>
            <Button
              disabled={!form.code || !form.name || !form.subject || !form.body || createMutation.isPending || updateMutation.isPending}
              onClick={() => {
                if (editTarget) {
                  updateMutation.mutate({ id: editTarget.id, dto: { name: form.name, subject: form.subject, body: form.body, fromAddress: form.fromAddress, notes: form.notes } });
                } else {
                  createMutation.mutate(form);
                }
              }}
            >
              {createMutation.isPending || updateMutation.isPending ? 'Saving...' : (editTarget ? 'Save Changes' : 'Create')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete template?</DialogTitle>
            <DialogDescription>
              <strong>{deleteTarget?.name}</strong> will be soft-deleted and will no longer appear in the whitelist or S49 dropdown.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" disabled={deleteMutation.isPending} onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}>
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview modal */}
      {previewTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-card rounded-xl shadow-2xl w-full max-w-3xl flex flex-col" style={{ maxHeight: '90vh' }}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-shrink-0">
              <div>
                <h2 className="font-semibold text-sm">{previewTemplate.name}</h2>
                <p className="text-xs text-muted-foreground mt-0.5">Subject: {previewTemplate.subject}</p>
              </div>
              <button onClick={() => setPreviewTemplate(null)} className="p-1 rounded hover:bg-accent transition-colors" aria-label="Close preview">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <iframe
                srcDoc={previewTemplate.body}
                title="Email preview"
                className="w-full h-full border-0"
                style={{ minHeight: 500 }}
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
